import { clsx } from "clsx";
import { HTMLAttributes, KeyboardEvent } from "react";
import { Icon } from "../Icons";
import { Tip, type TipProps } from "../Tip";
import { Heading, type HeadingProps } from "../Typography";
import "./TipsList.css";

export interface TipsListProps extends HTMLAttributes<HTMLDivElement> {
  tips?: TipProps[];
  title?: string;
  hideTitleIcon?: boolean;
  titleLevel?: HeadingProps["level"];
  actionLabel?: string;
  actionHeadingLevel?: HeadingProps["level"];
  onActionClick?: () => void;
  actionAriaLabel?: string;
}

export const TipsList = ({
  tips = [],
  title = "Pro tips",
  hideTitleIcon = false,
  titleLevel = 3,
  actionLabel = "Hide this tip",
  actionHeadingLevel = 4,
  onActionClick,
  actionAriaLabel,
  className,
  ...rest
}: TipsListProps) => {
  const handleActionKeyDown = (event: KeyboardEvent<HTMLHeadingElement>) => {
    if (!onActionClick) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onActionClick();
    }
  };

  const isActionInteractive = typeof onActionClick === "function";

  return (
    <div className={clsx("indigo-tips-list", className)} {...rest}>
      <div className="indigo-tips-list__header">
        {title ? (
          <div className="indigo-tips-list__title-group">
            {hideTitleIcon ? null : (
              <Icon
                name="lightbulb"
                size="xs"
                className="indigo-tips-list__icon"
              />
            )}
            <Heading level={titleLevel} className="indigo-tips-list__title">
              {title}
            </Heading>
          </div>
        ) : null}
        {actionLabel ? (
          <Heading
            level={actionHeadingLevel}
            className={clsx(
              "indigo-tips-list__action",
              isActionInteractive && "indigo-tips-list__action--interactive",
            )}
            role={isActionInteractive ? "button" : undefined}
            tabIndex={isActionInteractive ? 0 : undefined}
            onClick={isActionInteractive ? onActionClick : undefined}
            onKeyDown={isActionInteractive ? handleActionKeyDown : undefined}
            aria-label={
              isActionInteractive ? (actionAriaLabel ?? actionLabel) : undefined
            }
          >
            {actionLabel}
          </Heading>
        ) : null}
      </div>
      <div className="indigo-tips-list__items">
        {tips.map((tip, index) => (
          <Tip key={tip.id ?? index} {...tip} />
        ))}
      </div>
    </div>
  );
};

TipsList.displayName = "TipsList";
